import { fetchCountries } from 'api/Countries/actions'
import { fetchSaleTypes } from 'api/SaleTypes/actions'
import { ChannelsList } from 'ChannelsList'
import { ChannelSummary } from 'ChannelSummary'
import type { DetailsType } from 'ChannelSummary/constants'
import classNames from 'classnames'
import { CountriesList } from 'CountriesList'
import {
  selectChannelsType,
  selectRefetchParams,
  selectSaleTypesList,
  selectSubListTypeSelected,
  selectTabRequestParams,
} from 'data/selectors'
import { GroupDetails } from 'GroupDetails'
import { ListDetails } from 'ListDetails'
import { selectIsListDetailsOpen } from 'ListDetails/selectors'
import { LoadingOverlay } from 'LoadingOverlay'
import React, { memo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectLastReFetchTime } from 'store/reducers/lastReFetchTime'

import Box from '@mui/material/Box'

import useStyles from './useStyles'

interface Props {
  channel?: string
  countryId?: string
  detailsType?: DetailsType
  hideFlag?: boolean
  level?: number
}

function SaleTypesList({ channel, countryId, detailsType, hideFlag }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { isLoading, list } = useSelector(selectSaleTypesList({ channel, countryId }))
  const channelsType = useSelector(selectChannelsType)
  const lastReFetchTime = useSelector(selectLastReFetchTime)
  const refetchParams = useSelector(selectRefetchParams)
  const tabRequestParams = useSelector(selectTabRequestParams)
  const subListTypeSelected = useSelector(selectSubListTypeSelected)
  const showSkeletonLoader = list.length === 0 && isLoading
  const isSaleTypesSubList = subListTypeSelected === 'saleTypes'
  const isChannelsSubList = subListTypeSelected === 'channels'
  const isActive = channelsType === 'saleTypes' || isSaleTypesSubList
  const isToggled = useSelector(selectIsListDetailsOpen)

  useEffect(() => {
    if (isActive) {
      dispatch(
        fetchSaleTypes({
          ...tabRequestParams,
          channel,
          countryId,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, countryId, isActive, lastReFetchTime, refetchParams, dispatch])

  return (
    <LoadingOverlay isLoading={showSkeletonLoader} shouldHide={!isActive}>
      <Box minHeight={showSkeletonLoader ? '6rem' : '0'}>
        {list.map(data => {
          const { saleType } = data
          const fetchList = () => {
            if (isActive && subListTypeSelected === 'countries') {
              dispatch(fetchCountries({ ...tabRequestParams, saleType }))
            }
          }

          return (
            <ChannelSummary
              key={saleType}
              channel={channel}
              className={classNames({
                [classes.details]: isToggled,
                [classes.root]: channel || countryId,
              })}
              countryId={countryId}
              data={data}
              detailsType={detailsType}
              fetchList={fetchList}
              hideFlag={hideFlag}
              label={saleType}
              saleType={saleType}
              type="saleTypes"
            >
              {!isSaleTypesSubList && <ListDetails data={data} saleType={saleType} isLoading={isLoading} type="saleTypes" />}
              {isSaleTypesSubList && (
                <GroupDetails
                  channel={channel}
                  countryId={countryId}
                  data={data}
                  isLoading={isLoading}
                  saleType={saleType}
                  type="saleTypes"
                />
              )}
              {!isToggled && (
                <>
                  {isChannelsSubList && <ChannelsList saleType={saleType} />}
                  {!isSaleTypesSubList && !isChannelsSubList && <CountriesList saleType={saleType} />}
                </>
              )}
            </ChannelSummary>
          )
        })}
      </Box>
    </LoadingOverlay>
  )
}

export default memo(SaleTypesList)
